import { Vessel, PartInstance, partById, childrenOf, subtreeOf } from '../core/vessel/vessel'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { computeMassProperties } from '../core/vessel/massProperties'
import { fuelPathForEngine } from '../core/vessel/propellant'
import { G0, deltaV } from '../core/propulsion/engine'

export interface StageInfo {
  readonly stage: number
  /** motori che si accendono con questo stadio */
  readonly engines: string[]
  /** disaccoppiatori che scattano con questo stadio */
  readonly decouplers: string[]
}

/**
 * Gli stadi in ordine di attivazione. Convenzione: **lo stadio 0 parte per
 * primo**, poi 1, 2 e cosi via. Dentro uno stadio prima si sgancia e poi si
 * accende, altrimenti si accenderebbe un motore contro il pezzo che sta per
 * essere buttato via.
 */
export function stagesOf(vessel: Vessel, catalog: PartCatalog): StageInfo[] {
  const numeri = [...new Set(vessel.parts.map((p) => p.stage))].sort((a, b) => a - b)
  return numeri.map((stage) => {
    const parti = vessel.parts.filter((p) => p.stage === stage)
    return {
      stage,
      engines: parti.filter((p) => partDef(catalog, p.defId).engine).map((p) => p.id),
      decouplers: parti.filter((p) => partDef(catalog, p.defId).decoupler).map((p) => p.id),
    }
  })
}

export interface Separation {
  readonly remaining: Vessel
  readonly detached: Vessel[]
}

/**
 * Scatta un disaccoppiatore: tutto quello che gli e appeso sotto si stacca.
 * Il disaccoppiatore resta con la parte superiore.
 *
 * Le coordinate locali NON vengono ricalcolate: i due veicoli continuano a
 * vivere nello stesso sistema di riferimento, cosi lo stato del corpo rigido
 * si trasporta con una sola correzione di baricentro e la separazione non fa
 * saltare nulla.
 */
export function separate(vessel: Vessel, catalog: PartCatalog, decouplerId: string): Separation {
  const dec = partById(vessel, decouplerId)
  if (!partDef(catalog, dec.defId).decoupler) {
    throw new Error(`La parte ${decouplerId} non e un disaccoppiatore`)
  }

  const gruppi = childrenOf(vessel, decouplerId).map((figlio) => subtreeOf(vessel, figlio.id))
  const staccati = new Set(gruppi.flat().map((p) => p.id))

  const detached: Vessel[] = gruppi.map((parti) => ({
    rootId: parti[0]!.id,
    parts: parti.map((p): PartInstance => (p.id === parti[0]!.id ? { ...p, parentId: null } : p)),
  }))

  return {
    remaining: { rootId: vessel.rootId, parts: vessel.parts.filter((p) => !staccati.has(p.id)) },
    detached,
  }
}

export interface StagePerformance {
  readonly stage: number
  /** massa all'accensione dello stadio, kg */
  readonly wetMass: number
  /** massa a fine combustione, kg */
  readonly dryMass: number
  /** propellente consumato da questo stadio, kg */
  readonly propellant: number
  readonly thrustVacuum: number
  readonly thrustSeaLevel: number
  readonly twrSeaLevel: number
  /** delta-v nel vuoto, m/s */
  readonly deltaV: number
  /** secondi di combustione a piena manetta */
  readonly burnTime: number
}

/**
 * Delta-v e prestazioni stadio per stadio.
 *
 * Non si stima: si ripercorre davvero la sequenza di stadiazione su una copia
 * del veicolo, sganciando i pezzi e svuotando i serbatoi raggiungibili dai
 * motori accesi. Trattare il razzo come un solo stadio sottostima il delta-v
 * di un fattore due, ed e l'errore che fa progettare razzi che non arrivano.
 */
export function stagePerformance(vessel: Vessel, catalog: PartCatalog): StagePerformance[] {
  // copia profonda quanto basta: il propellente e l'unico campo mutabile
  let corrente: Vessel = { rootId: vessel.rootId, parts: vessel.parts.map((p) => ({ ...p })) }
  const accesi: string[] = []
  const out: StagePerformance[] = []

  for (const info of stagesOf(vessel, catalog)) {
    for (const decId of info.decouplers) {
      if (!corrente.parts.some((p) => p.id === decId)) continue
      corrente = separate(corrente, catalog, decId).remaining
    }
    const vivi = new Set(corrente.parts.map((p) => p.id))
    for (let i = accesi.length - 1; i >= 0; i--) if (!vivi.has(accesi[i]!)) accesi.splice(i, 1)
    for (const e of info.engines) if (vivi.has(e) && !accesi.includes(e)) accesi.push(e)

    const serbatoi = new Set<string>()
    for (const e of accesi) for (const t of fuelPathForEngine(corrente, catalog, e)) serbatoi.add(t)

    const propellant = [...serbatoi].reduce((s, id) => s + partById(corrente, id).propellant, 0)
    const wetMass = computeMassProperties(corrente, catalog).mass
    const dryMass = wetMass - propellant

    let thrustVacuum = 0, thrustSeaLevel = 0, flussoVac = 0
    for (const e of accesi) {
      const def = partDef(catalog, partById(corrente, e).defId).engine!
      thrustVacuum += def.thrustVac
      thrustSeaLevel += def.thrustSl
      flussoVac += def.thrustVac / (def.ispVac * G0)
    }
    // Isp equivalente del grappolo: la portata totale a spinta totale
    const ispEquivalente = flussoVac > 0 ? thrustVacuum / (flussoVac * G0) : 0

    out.push({
      stage: info.stage,
      wetMass,
      dryMass,
      propellant,
      thrustVacuum,
      thrustSeaLevel,
      twrSeaLevel: thrustSeaLevel / (wetMass * 9.81),
      deltaV: propellant > 0 ? deltaV(ispEquivalente, wetMass, dryMass) : 0,
      burnTime: flussoVac > 0 ? propellant / flussoVac : 0,
    })

    for (const id of serbatoi) partById(corrente, id).propellant = 0
  }

  return out
}

export const totalDeltaV = (vessel: Vessel, catalog: PartCatalog): number =>
  stagePerformance(vessel, catalog).reduce((s, p) => s + p.deltaV, 0)
